import Cache from '@fs/zion-cache'
import axios from '@fs/zion-axios'
import type { BasePerson, Contributor, PersonId } from '@fs/zion-tree-types'
import type { SwrResponse } from './useSwrRequest'
import { useSwrRequest, updateRequestMultiple } from './useSwrRequest'
import { swrKey as swrKeyChangeLogSummary } from './useChangeLogSummary'

export type TombstonedData = {
  contributor: Contributor
  deletedPerson: {
    id: PersonId
    name: string
  }
  justification: string
  restorable: boolean
  survivingPerson?: {
    id: PersonId | ''
    name: string
  }
  type: 'DELETED_BY_PERSON_DELETE' | 'DELETED_BY_PERSON_MERGE'
  unmergeable: boolean
}

const tombstonedDataCache = new Cache({
  storeName: 'tombstoned_data_r9',
  encrypted: process.env.NODE_ENV === 'production',
})
const swrKey = (pid: string): string => `/service/tree/tree-data/v8/person/${pid}/tombstone/metadata`

type Options = {
  /** A 5xx comes back as the `error` instead of the page error overlay, for a read the page can do without */
  silentServerErrors?: boolean
}

export default function useTombstonedData(
  person: Partial<BasePerson>,
  { silentServerErrors }: Options = {}
): SwrResponse<TombstonedData> & {
  tombstonedData: TombstonedData | undefined
} {
  const response = useSwrRequest<TombstonedData>(person?.id ? swrKey(person.id) : null, tombstonedDataCache, {
    revalidateIfStale: false,
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    silentServerErrors,
  })
  return { ...response, tombstonedData: response.data }
}

function mutateKeys(personId: PersonId): string[] {
  return [swrKey(personId), swrKeyChangeLogSummary(personId)]
}
type Props = {
  personId?: PersonId
  reason: string
}
export async function restorePerson({ personId, reason }: Props): Promise<void> {
  if (!personId) throw new Error('restorePerson called without a person ID')
  await updateRequestMultiple(mutateKeys(personId), () =>
    axios.put(`/service/tree/tree-data/v8/person/${personId}/restore`, { reason })
  )
}

export async function deletePerson({ personId, reason }: Props): Promise<void> {
  if (!personId) throw new Error('deletePerson called without a person ID')
  await updateRequestMultiple(mutateKeys(personId), () =>
    axios.delete(`/service/tree/tree-data/v8/person/${personId}`, {
      headers: { 'x-reason': reason },
    })
  )
}
